/**
 * Shared helpers for the Apply-page localStorage draft.
 *
 * Single draft per employee. Keyed by `employeeId` so impersonation /
 * multi-user-on-one-machine doesn't cross drafts. The form state shape
 * stored here is deliberately untyped at this layer — the Apply page
 * narrows it to `FormState` on read. Other surfaces (e.g. MyLeavePage
 * Hero card) only care about whether a draft EXISTS and when it was
 * saved.
 */

export const APPLY_DRAFT_VERSION = 1

export interface SavedApplyDraftMeta {
  version: number
  savedAt: string
}

export interface SavedApplyDraft<TForm = unknown, THalfDays = unknown>
  extends SavedApplyDraftMeta {
  form: TForm
  perDateHalfDays: THalfDays
}

export function applyDraftKey(employeeId: string | null): string | null {
  if (!employeeId) return null
  return `lms.apply-draft.v${APPLY_DRAFT_VERSION}.${employeeId}`
}

export function loadApplyDraft<TForm = unknown, TH = unknown>(
  employeeId: string | null,
): SavedApplyDraft<TForm, TH> | null {
  const key = applyDraftKey(employeeId)
  if (!key) return null
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SavedApplyDraft<TForm, TH>
    if (parsed.version !== APPLY_DRAFT_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

export function saveApplyDraft<TForm = unknown, TH = unknown>(
  employeeId: string | null,
  payload: Omit<SavedApplyDraft<TForm, TH>, 'version' | 'savedAt'>,
): SavedApplyDraft<TForm, TH> | null {
  const key = applyDraftKey(employeeId)
  if (!key) return null
  if (typeof window === 'undefined') return null
  const draft: SavedApplyDraft<TForm, TH> = {
    version: APPLY_DRAFT_VERSION,
    savedAt: new Date().toISOString(),
    ...payload,
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(draft))
    return draft
  } catch {
    return null
  }
}

export function clearApplyDraft(employeeId: string | null): void {
  const key = applyDraftKey(employeeId)
  if (!key) return
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(key)
  } catch {
    /* swallow — quota / private-mode */
  }
}

// ── Resume signal ─────────────────────────────────────────────────────────────
// Cross-page hint that says "the user clicked a Resume-draft link —
// auto-restore on next /apply mount". Backed by sessionStorage so it
// survives a hard browser navigation (the chip is a plain <a>) but
// doesn't outlive the tab. Decoupled from the URL search param because
// TanStack Router strips unknown params unless the route opts in.

const RESUME_FLAG_KEY = 'lms.apply-draft.resume-on-next-mount'

export function flagApplyDraftResume(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(RESUME_FLAG_KEY, '1')
  } catch {
    /* swallow */
  }
}

export function consumeApplyDraftResumeFlag(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const flag = window.sessionStorage.getItem(RESUME_FLAG_KEY) === '1'
    if (flag) window.sessionStorage.removeItem(RESUME_FLAG_KEY)
    return flag
  } catch {
    return false
  }
}

export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffMs = Date.now() - then
  const diffMin = Math.round(diffMs / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} min ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  return `${diffDay}d ago`
}
